/**
 * AppSettingsCard — small admin panel for editable app settings stored in
 * MongoDB (no .env edits needed for production).
 *
 * Current settings:
 *   - samcart_practcom_url  → CTA in the 14-day Smile→PractCom upsell email
 *   - samcart_smile_url     → reference for support
 *   - upsell_delay_days     → how soon the upsell fires after a standalone purchase
 *
 * Includes a "Preview upsell email" button that opens the live HTML in a new tab.
 */
import React, { useEffect, useState } from 'react';
import {
  Settings, Loader2, RefreshCw, AlertTriangle, Save,
  Eye, RotateCcw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AppSettingsCard({ adminToken }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/settings`, { headers });
      if (!res.ok) throw new Error(`Load failed: ${res.status}`);
      const data = await res.json();
      setItems(data.settings || []);
      setDraft(Object.fromEntries((data.settings || []).map(s => [s.key, s.value])));
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (key) => {
    setSavingKey(key);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/settings/${key}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: draft[key] }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Save failed (${res.status}): ${t.slice(0, 200)}`);
      }
      setToast(`Saved ${key}`);
      setTimeout(() => setToast(null), 2500);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const resetToDefault = async (key) => {
    if (!window.confirm(`Reset "${key}" back to its default value?`)) return;
    setSavingKey(key);
    try {
      const res = await fetch(`${API}/api/admin/settings/${key}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) throw new Error(`Reset failed: ${res.status}`);
      setToast(`Reset ${key} to default`);
      setTimeout(() => setToast(null), 2500);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const previewUpsellEmail = async () => {
    // The preview endpoint requires admin auth → fetch HTML then open via blob URL
    try {
      const res = await fetch(`${API}/api/admin/settings/preview/upsell-email`, { headers });
      if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'width=720,height=900');
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div data-testid="app-settings-card"
         className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="bg-amber-600 text-white rounded-lg p-2.5">
          <Settings className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-amber-900">App Settings</h3>
          <p className="text-xs text-amber-800 mt-0.5 max-w-3xl leading-snug">
            Edit these here instead of touching .env files. Changes take effect immediately
            (no redeploy needed). The Subscribe-to-PractCom button in the 14-day upsell
            email uses the URL below — keep it pointing at your live SamCart PractCom checkout.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={load} disabled={loading}
                className="bg-white border-2 border-amber-300 text-amber-900 text-sm font-bold px-3 py-1.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="settings-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={previewUpsellEmail}
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold px-3 py-1.5 rounded inline-flex items-center gap-1"
                data-testid="settings-preview-upsell">
          <Eye className="w-3.5 h-3.5" /> Preview upsell email
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="settings-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}
      {toast && (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 text-sm rounded-lg p-2" data-testid="settings-toast">
          {toast}
        </div>
      )}

      <div className="space-y-3" data-testid="settings-list">
        {items.map(s => {
          const isDirty = String(draft[s.key] ?? '') !== String(s.value ?? '');
          return (
            <div key={s.key} className="bg-white border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-extrabold text-slate-900 text-sm font-mono">{s.key}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{s.description}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Currently from <span className="font-bold">{s.source}</span>{' '}
                    {s.updatedAt && <>· last updated {new Date(s.updatedAt).toLocaleString()}</>}
                  </p>
                </div>
                <div className="flex-1 max-w-md">
                  {s.key === 'upsell_delay_days' ? (
                    <input
                      type="number"
                      min="0" max="365"
                      value={draft[s.key] ?? ''}
                      onChange={e => setDraft({ ...draft, [s.key]: e.target.value })}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded text-sm font-mono focus:border-amber-500 outline-none"
                      data-testid={`settings-input-${s.key}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={draft[s.key] ?? ''}
                      onChange={e => setDraft({ ...draft, [s.key]: e.target.value })}
                      placeholder={s.default}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded text-sm font-mono focus:border-amber-500 outline-none"
                      data-testid={`settings-input-${s.key}`}
                    />
                  )}
                  <p className="text-[10px] text-slate-400 mt-0.5 font-mono">default: {s.default}</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => save(s.key)}
                      disabled={!isDirty || savingKey === s.key}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`settings-save-${s.key}`}
                    >
                      {savingKey === s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save
                    </button>
                    <button
                      onClick={() => resetToDefault(s.key)}
                      disabled={s.source === 'default' || savingKey === s.key}
                      title="Remove the saved override; will fall back to the default value"
                      className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-bold px-3 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`settings-reset-${s.key}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Reset to default
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
